import type { EditionCategoryDetailResponse } from '@api/edition-categories-api';
import { getEditionCategories } from '@api/edition-categories-api';
import type { EditionResponse } from '@api/editions-api';
import { getEditions } from '@api/editions-api';
import type { NomineeDetailResponse } from '@api/nominees-api';
import { getNomineesByCategory } from '@api/nominees-api';
import type { UserResponse } from '@api/users-api';
import { createVote } from '@api/votes-api';
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  Building2,
  Check,
  Heart,
  Loader2,
  MapPin,
  Stethoscope,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

const AVATAR_COLORS = [
  '#007c68',
  '#2563eb',
  '#7c3aed',
  '#ea580c',
  '#dc2626',
  '#0891b2',
  '#4f46e5',
  '#b91c1c',
] as const;

type VoteState = 'idle' | 'loading' | 'success' | 'error';

function getAvatarColor(index: number): string {
  return AVATAR_COLORS[index % AVATAR_COLORS.length] ?? AVATAR_COLORS[0];
}

function getParticipantInitials(name: string, surname: string | null): string {
  if (!surname) return name.charAt(0).toUpperCase();
  return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();
}

function getEmailDisplayName(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getEmailInitials(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split('.');
  if (parts.length >= 2) {
    return `${parts[0]?.charAt(0) ?? ''}${parts[1]?.charAt(0) ?? ''}`.toUpperCase();
  }
  return (parts[0] ?? '').slice(0, 2).toUpperCase();
}

function NomineeCard({
  nominee,
  index,
  user,
}: {
  readonly nominee: NomineeDetailResponse;
  readonly index: number;
  readonly user: UserResponse;
}): React.JSX.Element {
  const [voteState, setVoteState] = useState<VoteState>('idle');

  const handleVote = useCallback(async (): Promise<void> => {
    if (voteState === 'loading' || voteState === 'success') return;

    setVoteState('loading');

    try {
      await createVote({
        nominationId: nominee.id,
        userId: user.id,
      });
      setVoteState('success');
    } catch {
      setVoteState('error');
      setTimeout(() => setVoteState('idle'), 2500);
    }
  }, [nominee.id, user.id, voteState]);

  const buttonContent = (): React.JSX.Element => {
    switch (voteState) {
      case 'loading':
        return (
          <>
            <Loader2 size={16} className="vs-vote-btn__spinner" />
            Votando...
          </>
        );
      case 'success':
        return (
          <>
            <Check size={16} />
            Voto registrado
          </>
        );
      case 'error':
        return <>Error al votar. Reintentar</>;
      default:
        return (
          <>
            <Heart size={16} />
            Votar
          </>
        );
    }
  };

  const fullName = [nominee.participant.name, nominee.participant.surname]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`vs-nominee-card vs-animate vs-animate--delay-${Math.min(index + 1, 5)} ${voteState === 'success' ? 'vs-nominee-card--voted' : ''}`}
    >
      {voteState === 'success' && (
        <div className="vs-confetti" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="vs-confetti__particle" />
          ))}
        </div>
      )}

      <div className="vs-nominee-card__header">
        <div className="vs-nominee-card__avatar" style={{ background: getAvatarColor(index) }}>
          {getParticipantInitials(nominee.participant.name, nominee.participant.surname)}
        </div>

        <div className="vs-nominee-card__info">
          <div className="vs-nominee-card__name">{fullName}</div>
          {nominee.participant.city && (
            <div className="vs-nominee-card__city">
              <MapPin size={13} />
              {nominee.participant.city}
            </div>
          )}
        </div>
      </div>

      <div className="vs-nominee-card__action">
        <button
          type="button"
          className={`vs-vote-btn vs-vote-btn--${voteState}`}
          onClick={handleVote}
          disabled={voteState === 'loading' || voteState === 'success'}
          aria-label={`Votar por ${fullName}`}
        >
          {buttonContent()}
        </button>
      </div>
    </div>
  );
}

function CategoryTabs({
  categories,
  activeId,
  onSelect,
}: {
  readonly categories: readonly EditionCategoryDetailResponse[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
}): React.JSX.Element {
  return (
    <div className="vs-categories">
      {categories.map((cat) => (
        <button
          key={cat.id}
          type="button"
          className={`vs-category-tab ${cat.id === activeId ? 'vs-category-tab--active' : ''}`}
          onClick={() => onSelect(cat.id)}
        >
          {cat.category.isClinic ? <Building2 size={16} /> : <Stethoscope size={16} />}
          {cat.category.details[0]?.name ?? cat.category.name}
          <span className="vs-category-tab__count">{cat.nomineeCount}</span>
        </button>
      ))}
    </div>
  );
}

function LoadingState(): React.JSX.Element {
  return (
    <div className="vs-loading">
      <Loader2 size={32} className="vs-vote-btn__spinner" />
      <p>Cargando información...</p>
    </div>
  );
}

function ErrorState({ message }: { readonly message: string }): React.JSX.Element {
  return (
    <div className="vs-loading">
      <AlertTriangle size={32} />
      <p>{message}</p>
    </div>
  );
}

export default function VotingDashboard(): React.JSX.Element {
  const location = useLocation();
  const user = (location.state as { user?: UserResponse })?.user;

  const [edition, setEdition] = useState<EditionResponse | null>(null);
  const [allCategories, setAllCategories] = useState<EditionCategoryDetailResponse[]>([]);
  const [nominees, setNominees] = useState<NomineeDetailResponse[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<string>('');
  const [loadingEdition, setLoadingEdition] = useState(true);
  const [loadingNominees, setLoadingNominees] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = allCategories;

  useEffect(() => {
    if (!user) return;

    async function fetchEditionData(): Promise<void> {
      try {
        const editionsResponse = await getEditions();
        const openEdition = editionsResponse.data.find((e) => e.status === 'open');

        if (!openEdition) {
          setError('No hay ninguna edición abierta.');
          setLoadingEdition(false);
          return;
        }

        setEdition(openEdition);

        const cats = await getEditionCategories(openEdition.id);
        setAllCategories(cats);
      } catch {
        setError('Error al cargar la edición.');
      } finally {
        setLoadingEdition(false);
      }
    }

    fetchEditionData();
  }, [user]);

  useEffect(() => {
    const first = categories[0];
    if (first && !categories.some((c) => c.id === activeCategoryId)) {
      setActiveCategoryId(first.id);
    }
  }, [categories, activeCategoryId]);

  useEffect(() => {
    if (!edition || !activeCategoryId) return;

    const activeEntry = categories.find((c) => c.id === activeCategoryId);
    if (!activeEntry) return;

    async function fetchNominees(): Promise<void> {
      setLoadingNominees(true);
      try {
        const response = await getNomineesByCategory(edition!.id, activeEntry!.category.id);
        setNominees(response.data);
      } catch {
        setNominees([]);
      } finally {
        setLoadingNominees(false);
      }
    }

    fetchNominees();
  }, [edition, activeCategoryId, categories]);

  if (!user) {
    return <Navigate to="/voting" replace />;
  }

  const activeCategory = categories.find((c) => c.id === activeCategoryId);

  return (
    <div className="vs-page">
      <nav className="vs-nav">
        <Link to="/voting" className="vs-nav__back">
          <ArrowLeft size={16} />
          Cambiar usuario
        </Link>

        <span className="vs-nav__brand">
          <span className="vs-nav__logo">
            <Award size={20} />
          </span>
          DP Awards
        </span>

        <div className="vs-nav__user">
          <span>{getEmailDisplayName(user.email)}</span>
          <span className="vs-nav__user-avatar">{getEmailInitials(user.email)}</span>
        </div>
      </nav>

      <div className="vs-dashboard">
        {loadingEdition && <LoadingState />}

        {error && <ErrorState message={error} />}

        {!loadingEdition && !error && edition && (
          <>
            <div className="vs-dashboard__hero vs-animate">
              <span className="vs-dashboard__edition-badge">
                <Award size={14} />
                {edition.country} · {edition.year}
              </span>
              <h1 className="vs-dashboard__title">{edition.name}</h1>
              <p className="vs-dashboard__subtitle">
                Explora las categorías y vota por tus profesionales favoritos.
              </p>
            </div>

            {categories.length > 0 && (
              <CategoryTabs
                categories={categories}
                activeId={activeCategoryId}
                onSelect={setActiveCategoryId}
              />
            )}

            {activeCategory && (
              <div className="vs-category-title vs-animate">
                <h2 className="vs-category-title__name">
                  {activeCategory.category.details[0]?.name ?? activeCategory.category.name}
                </h2>
                <p className="vs-category-title__count">
                  {activeCategory.nomineeCount} nominado{activeCategory.nomineeCount !== 1 && 's'}
                </p>
              </div>
            )}

            {loadingNominees && <LoadingState />}

            {!loadingNominees && nominees.length > 0 && (
              <div className="vs-nominees">
                {nominees.map((nominee, index) => (
                  <NomineeCard key={nominee.id} nominee={nominee} index={index} user={user} />
                ))}
              </div>
            )}

            {!loadingNominees && !loadingEdition && nominees.length === 0 && activeCategory && (
              <div className="vs-loading">
                <p>No hay nominados en esta categoría.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
